import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageContent from '../hooks/usePageContent';

export default function BowenTherapy() {
  const { page, loading } = usePageContent('bowen-terapi');

  useEffect(() => {
    if (page) {
      document.title = page.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.meta_description);
      }
    }
  }, [page]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-beige">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-brand-beige">
      {/* Page Header */}
      <section className="bg-brand-cream/30 py-12 md:py-16 border-b border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
            Behandlingsform
          </span>
          <h1 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl lg:text-5xl">
            {page.hero_title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
            {page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div 
                className="prose prose-stone text-sm leading-relaxed text-brand-neutral-dark/80"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
              
              {/* Realism / Disclaimer Box */}
              <div className="mt-6 rounded-2xl border border-brand-sage-medium/20 bg-brand-sage-light/30 p-6">
                <h3 className="font-serif text-base font-semibold text-brand-neutral-dark flex items-center gap-2">
                  <i className="fa-light fa-circle-info text-brand-sage" /> En realistisk tilgang til heling
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80">
                  Bowen Terapi understøtter kroppens egne processer, og reaktionerne er individuelle. Selvom mange oplever mærkbar lindring og dyb afspænding, kan der aldrig stilles garantier for specifikke resultater eller loves helbredelse. Behandlingen bør ses som et nænsomt supplement til kroppens naturlige balance og velvære.
                </p>
              </div>
            </div>

            {/* Right Sidebar Column */}
            <div className="lg:col-span-5 flex flex-col gap-8 lg:sticky lg:top-24">
              
              {/* Image */}
              <div className="overflow-hidden rounded-3xl shadow-md bg-white/40">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/d7ec2ecb-d3f6-4a99-ad2f-51a97712ea7d/475229915-1157986399449476-6645532474984307171-n.jpg"
                  alt="Blid behandling i klinikken"
                  className="w-full h-auto block"
                />
              </div>

              {/* Quick Facts Box */}
              <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/60 p-6 shadow-sm">
                <h3 className="font-serif text-base font-semibold text-brand-neutral-dark">Kort om Bowen Terapi</h3>
                <ul className="mt-4 flex flex-col gap-3 text-xs text-brand-neutral-dark/80">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-light fa-circle-check mt-0.5 text-brand-sage" />
                    <span><strong>Nænsomme greb:</strong> Små rullende tryk over muskler og sener.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-light fa-circle-check mt-0.5 text-brand-sage" />
                    <span><strong>Vigtige pauser:</strong> Nervesystemet får ro til at integrere impulserne.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-light fa-circle-check mt-0.5 text-brand-sage" />
                    <span><strong>Ingen manipulation:</strong> Ingen hårde tryk eller knæk af led.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-light fa-circle-check mt-0.5 text-brand-sage" />
                    <span><strong>For alle:</strong> Velegnet til både spædbørn, gravide, voksne og ældre.</span>
                  </li>
                </ul>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-cream/30 py-12 border-t border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center flex flex-col items-center gap-4">
          <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark">
            Vil du vide, hvordan en behandling foregår?
          </h2>
          <p className="text-sm text-brand-neutral-dark/80 max-w-xl">
            Læs mere om modtagelsen, selve behandlingen og hvad du kan forvente under dit besøg i klinikken.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            <Link
              to="/behandlingen"
              className="inline-flex items-center justify-center rounded-full bg-brand-sage px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark"
            >
              Læs om behandlingsforløbet
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-full border border-brand-sage-medium/30 bg-white/40 px-6 py-2.5 text-sm font-medium text-brand-neutral-dark transition-all duration-300 hover:bg-white/80"
            >
              Book en tid
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
